import { useEffect, useRef, useState } from 'react'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || '/api'

/**
 * Where somebody lands after signing in through their own company's system.
 *
 * The identity provider answers by redirecting a browser, which cannot receive JSON — so
 * something has to arrive in the address bar. What arrives is a ONE-TIME CODE, never a session
 * token: a real token in a URL ends up in browser history, in any proxy log along the way, and
 * in the Referer header of the next request. This page swaps the code for the real session with
 * a POST, which leaves no trace anywhere, and clears the address bar immediately either way.
 */
export default function OrgSsoLanding() {
  const started = useRef(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Guarded because React runs effects twice in development, and the code is single-use — the
    // second attempt would fail and show an error for a sign-in that actually worked.
    if (started.current) return
    started.current = true

    const params = new URLSearchParams(window.location.search || '')
    const code = params.get('code')
    // Removed before anything else happens, so it is gone from the address bar and from history
    // whether the exchange succeeds or not.
    window.history.replaceState(null, '', window.location.pathname)

    if (!code) {
      setError('This sign-in link is incomplete. Please start again from the sign-in page.')
      return
    }

    axios.post(`${API_URL}/organisations/oidc/exchange`, { code })
      .then(({ data }) => {
        // The same shape the ordinary sign-in stores, so everything downstream behaves
        // identically and nothing has to know how this person arrived.
        localStorage.setItem('user', JSON.stringify({
          access_token: data.access_token,
          token_type: data.token_type,
          role: data.role,
          full_name: data.full_name,
        }))
        // A full navigation, not a route change: the auth provider reads localStorage once on
        // mount, so a soft transition would leave it holding the previous session.
        window.location.replace(data.redirect_to || '/dashboard')
      })
      .catch(e => {
        setError(e.response?.data?.detail
          || 'That sign-in could not be completed. Please try again from the sign-in page.')
      })
  }, [])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-sm w-full text-center">
        {error ? (
          <>
            <div className="text-3xl mb-3">🔒</div>
            <h1 className="text-lg font-semibold text-gray-900">Sign-in not completed</h1>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
            <a href="/login"
               className="inline-block mt-5 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
              Back to sign in
            </a>
          </>
        ) : (
          <>
            <div className="w-10 h-10 mx-auto mb-4 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-600">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  )
}
